import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import type { Offer, OfferContingencies, OfferStatus } from '../lib/types';
import { AUTH_ENABLED } from '../lib/auth';
const noopGetToken = async () => null;

export function useOffers(
  propertyId: string | undefined,
  getToken: () => Promise<string | null> = noopGetToken
) {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOffers = useCallback(async () => {
    if (!AUTH_ENABLED || !propertyId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await apiFetch<Offer[]>(`/properties/${propertyId}/offers`, {}, getToken);
      setOffers(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [propertyId, getToken]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const createOffer = useCallback(async (body: {
    analysisId: string;
    offerPrice: number;
    profileId: string;
    inputs: any;
    results: any;
    earnestMoney: number;
    closingDate: string;
    contingencies: OfferContingencies;
    notes: string;
    status: OfferStatus;
  }): Promise<Offer> => {
    const offer = await apiFetch<Offer>(`/properties/${propertyId}/offers`, {
      method: 'POST',
      body: JSON.stringify(body),
    }, getToken);
    setOffers(prev => [offer, ...prev]);
    return offer;
  }, [propertyId, getToken]);

  const updateOffer = useCallback(async (offerId: string, body: Partial<Offer>): Promise<Offer> => {
    const offer = await apiFetch<Offer>(`/properties/${propertyId}/offers/${offerId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, getToken);
    setOffers(prev => prev.map(o => o.id === offerId ? offer : o));
    return offer;
  }, [propertyId, getToken]);

  const deleteOffer = useCallback(async (offerId: string) => {
    await apiFetch(`/properties/${propertyId}/offers/${offerId}`, { method: 'DELETE' }, getToken);
    setOffers(prev => prev.filter(o => o.id !== offerId));
  }, [propertyId, getToken]);

  return { offers, loading, error, fetchOffers, createOffer, updateOffer, deleteOffer };
}

export function useOfferDetail(
  propertyId: string | undefined,
  offerId: string | undefined,
  getToken: () => Promise<string | null> = noopGetToken
) {
  const [offer, setOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOffer = useCallback(async () => {
    if (!AUTH_ENABLED || !propertyId || !offerId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await apiFetch<Offer>(`/properties/${propertyId}/offers/${offerId}`, {}, getToken);
      setOffer(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [propertyId, offerId, getToken]);

  useEffect(() => { fetchOffer(); }, [fetchOffer]);

  const updateOffer = useCallback(async (body: Partial<Offer>): Promise<Offer> => {
    const updated = await apiFetch<Offer>(`/properties/${propertyId}/offers/${offerId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, getToken);
    setOffer(updated);
    return updated;
  }, [propertyId, offerId, getToken]);

  return { offer, loading, error, updateOffer, refetch: fetchOffer };
}
